/**
 * smart-composer.ts — 動態組卷 (LEARNING_SYSTEM_DESIGN_v2 §0 #3, #4, #13).
 *
 * v2 vertical slice — currently only `math.div.remainder`. Unlike
 * `entry/paper-composer.ts` (which samples a static bank by a fixed d1/d2/d3
 * ratio and NEVER reads `studentKPs`), this composer:
 *
 *   1. READS the learner's Leitner box for the target KP from
 *      `progress.studentKPs` (§0 #13: composer must be Leitner-aware).
 *   2. Picks difficulty + advanced-question ratio from that box (§0 #3):
 *        box ≤ 2  → all basic 同類題, advanced = 0 (early learning, no
 *                   frustration);
 *        box ≥ 3  → may append ONE ★challenge (advanced) question at the tail,
 *                   skippable, capped at ≤ 10% of the paper.
 *   3. Orders confidence-first (§0 #4): easier questions up front, the
 *      challenge (if any) always last.
 *   4. Generates every question through the div-remainder generator so values
 *      stay locked to the G3 curriculum guardrail; each answer is
 *      program-verifiable (被除數 = 商 × 除數 + 餘數).
 *
 * Pure + deterministic: same (progress box, opts) ⇒ same paper.
 */

import type { PlayerProgress } from '../../shared/types';
import type { EntryQuestion } from '../entry/question-source';
import { generateDivRemainder } from '../generators/math/div-remainder';
import type { Difficulty } from '../generators/math/types';

/** Supported KP set for this slice (widen as more generators get wired). */
export type SmartComposerKP = 'math.div.remainder';

export interface ComposePracticePaperOpts {
  subject: 'math';
  kp: SmartComposerKP;
  /** 8–12 題 per §0 #12; defaults to 10. */
  targetCount?: number;
}

/**
 * A practice-paper question. Structurally a `fill` `EntryQuestion` (so it drops
 * straight into the existing 考卷 layout) plus an `isChallenge` flag marking the
 * optional skippable ★ advanced question (§0 #3).
 */
export interface SmartPaperQuestion {
  kind: 'fill';
  prompt: string;
  correctAnswer: string;
  acceptedAnswers?: string[];
  explanation?: string;
  kp: string;
  surfaceVariant?: string;
  difficulty: 1 | 2 | 3;
  /** True only for the tail ★挑戰題 (box ≥ 3). Skippable; not part of「完成」. */
  isChallenge?: boolean;
}

export interface PracticePaper {
  title: string;
  questions: SmartPaperQuestion[];
  meta: {
    kp: SmartComposerKP;
    /** Leitner box the paper was composed against. */
    box: number;
    targetCount: number;
    /** How many tail ★challenge questions were included (0 or 1). */
    challengeCount: number;
  };
}

const DEFAULT_TARGET_COUNT = 10;
const MIN_TARGET_COUNT = 8;
const MAX_TARGET_COUNT = 12;

const KP_TITLES: Record<SmartComposerKP, string> = {
  'math.div.remainder': '除法餘數 · 練習卷',
};

/**
 * Compose a single-KP practice paper, Leitner-aware (§0 #3, #4, #13).
 *
 * Reads (never mutates) `progress.studentKPs` to find the target KP's box.
 */
export function composePracticePaper(
  progress: PlayerProgress,
  opts: ComposePracticePaperOpts,
): PracticePaper {
  const targetCount = clampCount(opts.targetCount ?? DEFAULT_TARGET_COUNT);
  const box = readBox(progress, opts.kp);

  // §0 #3: advanced (challenge) gating — only box ≥ 3, ≤ 10% of paper, ≥ 1 base.
  const allowChallenge = box >= 3;
  const challengeCount = allowChallenge
    ? Math.min(Math.floor(targetCount * 0.1), targetCount - 1) >= 1
      ? 1
      : 0
    : 0;
  const baseCount = targetCount - challengeCount;

  // Deterministic seed derived from KP + box so re-composing is reproducible
  // yet a box change reshuffles the surface.
  const baseSeed = hashSeed(`${opts.kp}:box${box}:n${targetCount}`);

  // §0 #4 confidence-first: base questions ramp gently. box ≤ 2 stays all-d1
  // (基礎同類題); box ≥ 3 mixes d1/d2 with the easier ones up front.
  const questions: SmartPaperQuestion[] = [];
  for (let i = 0; i < baseCount; i++) {
    const difficulty = baseDifficulty(box, i, baseCount);
    questions.push(toQuestion(generate(baseSeed + i, difficulty), difficulty, false));
  }

  // Confidence-first ordering: ascending difficulty (ties keep generation order).
  questions.sort((a, b) => a.difficulty - b.difficulty);

  // §0 #3: the ★challenge always lands at the very tail (after the sort).
  if (challengeCount === 1) {
    questions.push(toQuestion(generate(baseSeed + 9001, 3), 3, true));
  }

  return {
    title: KP_TITLES[opts.kp],
    questions,
    meta: { kp: opts.kp, box, targetCount, challengeCount },
  };
}

/* ─────────────────────────────────────────────────────────────────────────
   Internal helpers
   ───────────────────────────────────────────────────────────────────────── */

/** Read the learner's Leitner box for `kp` (defaults to 1 when unseen). */
function readBox(progress: PlayerProgress, kp: SmartComposerKP): number {
  const found = progress.studentKPs?.find((k) => k.kpId === kp);
  return found?.box ?? 1;
}

/** Clamp targetCount into the §0 #12 8–12 band. */
function clampCount(n: number): number {
  if (!Number.isFinite(n)) return DEFAULT_TARGET_COUNT;
  return Math.max(MIN_TARGET_COUNT, Math.min(MAX_TARGET_COUNT, Math.round(n)));
}

/**
 * Base-question difficulty by box + position (§0 #3/#4).
 * - box ≤ 2: every base question is d1 (基礎同類題, advanced = 0).
 * - box ≥ 3: first ~60% d1 (信心優先), the rest d2.
 */
function baseDifficulty(box: number, index: number, total: number): Difficulty {
  if (box <= 2) return 1;
  const d1Cut = Math.ceil(total * 0.6);
  return index < d1Cut ? 1 : 2;
}

/** Generate one div-remainder question at the given difficulty. */
function generate(seed: number, difficulty: Difficulty): ReturnType<typeof generateDivRemainder> {
  return generateDivRemainder(seed, difficulty);
}

/** Map a generator `FillQuestion` into a paper-ready `SmartPaperQuestion`. */
function toQuestion(
  q: ReturnType<typeof generateDivRemainder>,
  difficulty: 1 | 2 | 3,
  isChallenge: boolean,
): SmartPaperQuestion {
  return {
    kind: 'fill',
    prompt: q.prompt,
    correctAnswer: q.correctAnswer,
    acceptedAnswers: q.acceptedAnswers,
    explanation: q.explanation,
    kp: q.kp,
    surfaceVariant: q.prompt,
    difficulty,
    ...(isChallenge ? { isChallenge: true } : {}),
  };
}

/** FNV-1a string hash → 32-bit int seed (matches question-source convention). */
function hashSeed(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Type-level assurance that a `SmartPaperQuestion` is assignable to the fill
 * arm of `EntryQuestion` (so the existing 考卷 layout can consume it directly).
 * Compile-time only — erased at build.
 */
export type _SmartPaperQuestionIsEntryQuestion = SmartPaperQuestion extends EntryQuestion
  ? true
  : never;
